import { hasFirebaseConfig } from '../config/firebase';
import { getCurrentUser } from '../services/auth';
import { loadFirebase } from './roomClient/firebaseRuntime';
import {
    createInMemoryRoomMeta,
    getCachedLastRoll,
    getInMemoryPlayer,
    getInMemoryRoomMeta,
    hasInMemoryRoom,
    listenInMemoryRoomMeta,
    listenInMemoryPlayers,
    removeInMemoryPlayer,
    setCachedLastRoll,
    updateInMemoryRoomMeta,
    upsertInMemoryPlayer,
} from './roomClient/inMemoryStore';
import { PlayerPresence, RollGroup, RoomMeta, Unsubscribe } from './roomClient/types';
import { CharacterSheet } from '../types/CharacterSheet';
import { DEFAULT_ROOM_THEME, RoomTheme } from '../types/RoomTheme';
import { DiceConfig, DEFAULT_DICE_CONFIG, DEFAULT_ROOM_MODE, DiceType, RoomMode } from '../types/RoomMode';

function isPermissionDeniedError(error: any) {
    const code = String(error?.code ?? '').toLowerCase();
    return code.includes('permission') && code.includes('denied');
}

function createPresence(
    name: string,
    role: 'gm' | 'player',
    characterId: string | null | undefined,
    lastSeen: number
): Partial<PlayerPresence> {
    return {
        name,
        role,
        characterId: characterId ?? null,
        lastSeen,
    };
}

export function createRoomId(preferred?: string) {
    if (preferred && preferred.trim().length > 0) {
        return preferred.trim().replace(/\s+/g, '-');
    }
    return Math.random().toString(36).slice(2, 9);
}

export async function roomExists(roomId: string): Promise<boolean> {
    if (!roomId.trim()) return false;

    if (!hasFirebaseConfig) {
        return hasInMemoryRoom(roomId);
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, get } = fbModules;
    const roomRef = ref(fbDb, `rooms/${roomId}/meta`);
    const snap = await get(roomRef);
    return snap.exists();
}

export async function createRoom(
    roomId: string,
    gmId: string,
    theme: RoomTheme = DEFAULT_ROOM_THEME,
    mode: RoomMode = DEFAULT_ROOM_MODE,
    diceConfig: DiceConfig = DEFAULT_DICE_CONFIG
): Promise<{ roomId: string; created: boolean }> {
    const normalized = roomId.trim();
    if (!normalized) return { roomId: '', created: false };

    const meta: RoomMeta = {
        gmId,
        createdAt: Date.now(),
        name: normalized,
        theme,
        mode,
        diceConfig,
    };

    if (!hasFirebaseConfig) {
        const created = createInMemoryRoomMeta(normalized, meta);
        return { roomId: normalized, created };
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, get, set } = fbModules;
    const metaRef = ref(fbDb, `rooms/${normalized}/meta`);
    const existing = await get(metaRef);
    if (existing.exists()) {
        return { roomId: normalized, created: false };
    }

    await set(metaRef, meta);
    return { roomId: normalized, created: true };
}

export async function joinRoom(roomId: string, playerId: string, name: string, characterId?: string | null): Promise<() => void> {
    const now = Date.now();

    if (!hasFirebaseConfig) {
        const meta = getInMemoryRoomMeta(roomId);
        if (!meta) {
            throw new Error(`Room "${roomId}" does not exist`);
        }
        const existing = getInMemoryPlayer(roomId, playerId);
        const role: 'gm' | 'player' = meta.gmId === playerId ? 'gm' : 'player';
        upsertInMemoryPlayer(roomId, playerId, {
            name,
            role,
            characterId: characterId ?? null,
            characterSheet: existing?.characterSheet ?? null,
            lastSeen: now,
            lastRoll: existing?.lastRoll ?? null,
        });
        return () => {};
    }

    const user = getCurrentUser();
    if (!user) {
        throw new Error('User not authenticated yet');
    }

    const meta = await getRoomMeta(roomId);
    if (!meta) {
        throw new Error(`Room "${roomId}" does not exist`);
    }
    const role: 'gm' | 'player' = meta.gmId === user.uid ? 'gm' : 'player';

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, set, update, onDisconnect, onValue } = fbModules;
    const playerRef = ref(fbDb, `rooms/${roomId}/players/${user.uid}`);
    const connectedRef = ref(fbDb, '.info/connected');

    const initialPresence = createPresence(name, role, characterId, now);
    const cachedInitialRoll = getCachedLastRoll(roomId, user.uid) ?? getCachedLastRoll(roomId, playerId);
    if (cachedInitialRoll) {
        initialPresence.lastRoll = cachedInitialRoll;
    }
    await update(playerRef, initialPresence);

    const connectedUnsub = onValue(connectedRef, async (snap: any) => {
        if (snap.val() !== true) return;
        try {
            const od = onDisconnect(playerRef);
            await od.remove();
            const reconnectPresence = createPresence(name, role, characterId, Date.now());
            const reconnectLastRoll = getCachedLastRoll(roomId, user.uid) ?? getCachedLastRoll(roomId, playerId);
            if (reconnectLastRoll) {
                reconnectPresence.lastRoll = reconnectLastRoll;
            }
            await update(playerRef, reconnectPresence);
        } catch (error) {
            console.warn('Reconnect presence sync failed:', error);
        }
    });

    try {
        const od = onDisconnect(playerRef);
        await od.remove();
    } catch {
        // ignore onDisconnect errors (e.g. in some environments)
    }

    const heartbeatInterval = setInterval(async () => {
        try {
            const lastSeenRef = ref(fbDb, `rooms/${roomId}/players/${user.uid}/lastSeen`);
            await set(lastSeenRef, Date.now());
        } catch (error) {
            console.warn('Heartbeat failed:', error);
        }
    }, 30000);

    return () => {
        clearInterval(heartbeatInterval);
        if (typeof connectedUnsub === 'function') {
            try { connectedUnsub(); } catch { /* ignore */ }
        }
    };
}

export async function leaveRoom(roomId: string, playerId: string) {
    if (!hasFirebaseConfig) {
        removeInMemoryPlayer(roomId, playerId);
        return;
    }

    const user = getCurrentUser();
    if (!user) {
        console.warn('leaveRoom skipped: user not authenticated');
        return;
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, remove } = fbModules;
    const pRef = ref(fbDb, `rooms/${roomId}/players/${user.uid}`);
    await remove(pRef);
}

export async function getRoomMeta(roomId: string): Promise<RoomMeta | null> {
    if (!roomId.trim()) return null;

    if (!hasFirebaseConfig) {
        return getInMemoryRoomMeta(roomId);
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, get } = fbModules;
    const metaRef = ref(fbDb, `rooms/${roomId}/meta`);
    const snap = await get(metaRef);
    return snap.exists() ? (snap.val() as RoomMeta) : null;
}

export async function publishRoll(
    roomId: string,
    playerId: string,
    payload: { chains: number[][]; difficulty?: number; diceType?: DiceType; rollGroups?: RollGroup[]; timestamp?: number }
) {
    const now = typeof payload.timestamp === 'number' ? payload.timestamp : Date.now();

    if (!hasFirebaseConfig) {
        const lastRoll = {
            chains: payload.chains,
            difficulty: payload.difficulty ?? null,
            diceType: payload.diceType ?? null,
            rollGroups: payload.rollGroups ?? null,
            timestamp: now,
        };
        const existing = getInMemoryPlayer(roomId, playerId) ?? {
            name: 'Anónimo',
            characterId: null,
            lastSeen: now,
            lastRoll: null,
        };
        existing.lastRoll = lastRoll;
        existing.lastSeen = now;
        setCachedLastRoll(roomId, playerId, lastRoll);
        upsertInMemoryPlayer(roomId, playerId, existing);
        return;
    }

    const user = getCurrentUser();
    if (!user) {
        console.warn('publishRoll skipped: user not authenticated');
        return;
    }

    const lastRoll = {
        chains: payload.chains,
        difficulty: payload.difficulty ?? null,
        diceType: payload.diceType ?? null,
        rollGroups: payload.rollGroups ?? null,
        timestamp: now,
    };
    setCachedLastRoll(roomId, user.uid, lastRoll);
    if (playerId !== user.uid) {
        setCachedLastRoll(roomId, playerId, lastRoll);
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, set } = fbModules;
    const rRef = ref(fbDb, `rooms/${roomId}/players/${user.uid}/lastRoll`);
    await set(rRef, lastRoll);
}

export async function publishCharacterSheet(
    roomId: string,
    playerId: string,
    sheet: CharacterSheet | null
) {
    if (!hasFirebaseConfig) {
        const now = Date.now();
        const existing = getInMemoryPlayer(roomId, playerId) ?? {
            name: 'Anónimo',
            role: 'player',
            characterId: null,
            characterSheet: null,
            lastSeen: now,
            lastRoll: null,
        };
        existing.characterSheet = sheet;
        existing.lastSeen = now;
        upsertInMemoryPlayer(roomId, playerId, existing);
        return;
    }

    const user = getCurrentUser();
    if (!user) {
        console.warn('publishCharacterSheet skipped: user not authenticated');
        return;
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, set } = fbModules;
    const characterSheetRef = ref(fbDb, `rooms/${roomId}/players/${user.uid}/characterSheet`);
    await set(characterSheetRef, sheet);
}

export async function isRoomGm(roomId: string, playerId: string): Promise<boolean> {
    const meta = await getRoomMeta(roomId);
    return Boolean(meta && meta.gmId === playerId);
}

export async function updateRoomTheme(roomId: string, playerId: string, theme: RoomTheme): Promise<boolean> {
    const meta = await getRoomMeta(roomId);
    if (!meta || meta.gmId !== playerId) {
        return false;
    }

    if (!hasFirebaseConfig) {
        const current = getInMemoryRoomMeta(roomId);
        if (!current) return false;
        return updateInMemoryRoomMeta(roomId, { ...current, theme });
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, set } = fbModules;
    const themeRef = ref(fbDb, `rooms/${roomId}/meta/theme`);
    await set(themeRef, theme);
    return true;
}

export async function updateDiceConfig(roomId: string, playerId: string, diceConfig: DiceConfig): Promise<boolean> {
    const meta = await getRoomMeta(roomId);
    if (!meta || meta.gmId !== playerId) {
        return false;
    }

    if (!hasFirebaseConfig) {
        const current = getInMemoryRoomMeta(roomId);
        if (!current) return false;
        return updateInMemoryRoomMeta(roomId, { ...current, diceConfig });
    }

    const { fbModules, fbDb } = await loadFirebase();
    const { ref, set } = fbModules;
    const configRef = ref(fbDb, `rooms/${roomId}/meta/diceConfig`);
    await set(configRef, diceConfig);
    return true;
}

export function listenRoomMeta(roomId: string, cb: (meta: RoomMeta | null) => void): Unsubscribe {
    if (!hasFirebaseConfig) {
        return listenInMemoryRoomMeta(roomId, cb);
    }

    let firebaseUnsub: Unsubscribe | null = null;
    let mounted = true;

    const setupListener = async () => {
        try {
            if (!mounted) return;
            const { fbModules, fbDb } = await loadFirebase();
            if (!mounted) return;
            const { ref, onValue } = fbModules;
            const roomMetaRef = ref(fbDb, `rooms/${roomId}/meta`);
            firebaseUnsub = onValue(roomMetaRef, (snap: any) => {
                const val = snap.val();
                cb(val ? (val as RoomMeta) : null);
            });
        } catch (error) {
            console.error('Failed to setup room meta listener:', error);
            cb(null);
        }
    };

    void setupListener();

    return () => {
        mounted = false;
        if (typeof firebaseUnsub === 'function') {
            try { firebaseUnsub(); } catch { /* ignore */ }
            firebaseUnsub = null;
        }
    };
}

export function listenPlayers(roomId: string, cb: (players: Record<string, PlayerPresence>) => void): Unsubscribe {
    if (!hasFirebaseConfig) {
        return listenInMemoryPlayers(roomId, cb);
    }

    let firebaseUnsub: Unsubscribe | null = null;
    let mounted = true;
    let retryCount = 0;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    const maxRetries = 5;
    const baseRetryDelay = 2000;

    const clearRetry = () => {
        if (retryTimeout) {
            clearTimeout(retryTimeout);
            retryTimeout = null;
        }
    };

    const scheduleRetry = () => {
        if (!mounted || retryCount >= maxRetries || retryTimeout) return;
        retryCount++;
        const delay = baseRetryDelay * Math.pow(2, retryCount - 1);
        console.log(`Retrying listener for ${roomId} in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
        retryTimeout = setTimeout(() => {
            retryTimeout = null;
            if (mounted) {
                void setupListener();
            }
        }, delay);
    };

    const setupListener = async () => {
        try {
            if (!mounted) return;
            const { fbModules, fbDb } = await loadFirebase();
            if (!mounted) return;
            const { ref, onValue } = fbModules;
            const roomPlayersRef = ref(fbDb, `rooms/${roomId}/players`);

            if (typeof firebaseUnsub === 'function') {
                try { firebaseUnsub(); } catch { /* ignore */ }
                firebaseUnsub = null;
            }

            firebaseUnsub = onValue(
                roomPlayersRef,
                (snap: any) => {
                    const val = snap.val() ?? {};
                    try { cb(val); } catch { /* ignore cb errors */ }
                    retryCount = 0;
                    clearRetry();
                },
                (error: any) => {
                    console.warn(`Firebase listener error for room ${roomId}:`, error);
                    if (!mounted || isPermissionDeniedError(error)) return;
                    if (typeof firebaseUnsub === 'function') {
                        try { firebaseUnsub(); } catch { /* ignore */ }
                        firebaseUnsub = null;
                    }
                    scheduleRetry();
                }
            );
        } catch (error) {
            console.error('Failed to setup Firebase listener:', error);
            scheduleRetry();
        }
    };

    void setupListener();

    return () => {
        mounted = false;
        clearRetry();
        if (typeof firebaseUnsub === 'function') {
            try { firebaseUnsub(); } catch { /* ignore */ }
            firebaseUnsub = null;
        }
    };
}
